import { Euler, Group, MathUtils, Vector2, Vector3 } from 'three'
import LayerKeys from '~/config/layerKeys'
import { createAppSelector, useAppDispatch, useAppSelector } from '~/store/hooks'
import { Obstruction } from '~/store/objects/types'
import {
  modelToUI,
  scaleModelVectorToUI,
  scaleUIVectorToModel,
  uiToModel,
  vectorModelToUI,
  vectorUIToModel,
} from '../util/units'
import { BoxDragControl } from '../util/boxDragControls'
import { cornerPoints } from '../util/boxDragUtil'
import { Line, ScreenSizer } from '@react-three/drei'
import { memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { selectObjects } from '~/store/selectedObjects'
import ObjectClassName from '~/config/objectClassNames'
import { transformMatrix } from '../ComfortZones/utils'
import { updateObstruction } from '~/store/objects'
import { productDistanceEngine } from '../lib/productDistanceEngine'
import { checkInFacility } from './util'
import Util from '../lib/util'
import { LoadingIndicator } from '../Products/components/LoadingIndicator'
import { DragControls } from '../util/DragControls'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'
import { ObstructionClearanceMesh } from '~/components/DrawingCanvas/Obstructions/components/ObstructionClearanceMesh'
import { RotationHandle } from '~/components/DrawingCanvas/Obstructions/components/RotationHandle'
import { ArrowHandle, DimensionGuides } from '~/components/DrawingCanvas/Obstructions/components/DimensionGuides'
import { useModifiers } from '~/components/DrawingCanvas/hooks/modifiers'
import { useCardinalSnaplines } from '~/components/DrawingCanvas/Products/hooks/useCardinalSnaplines'
import { DetailedObstructionMesh } from '~/components/DrawingCanvas/Obstructions/components/DetailedObstructionMesh'
import { SnapData, SnapPoint, snapToPointOrtho } from '~/components/DrawingCanvas/util/snaplines'
import {
  ObstructionSnaplines,
  ObstructionsSnaplinesRef,
} from '~/components/DrawingCanvas/Obstructions/components/ObstructionSnaplines'

const SNAP_LINE_Z_OFFSET = 3
const SNAP_THRESHOLD = 10000
const RESIZE_HANDLE_COUNT = 8
const RESIZE_HANDLES = Array(RESIZE_HANDLE_COUNT)
  .fill(null)
  .map((_, i) => i)
const RESIZE_HANDLE_Z_OFFSET = new Vector3(0, 0, 0.5)

export function Obstructions() {
  const isVisible = useAppSelector(it => it.layers.layers[LayerKeys.OBSTRUCTIONS].visible)
  const obstructions = useAppSelector(it => it.objects.present.obstructions)

  return (
    <group visible={isVisible} name={LayerKeys.OBSTRUCTIONS}>
      {Object.entries(obstructions).map(([key, value]) => (
        <MemoizedObstruction key={key} obstruction={value} />
      ))}
    </group>
  )
}

const MemoizedObstruction = memo(AnObstruction)

const selectOtherObstructionsPositions = createAppSelector(
  [state => state.objects.present.obstructions, (_, id: string) => id],
  (obstructions, id) =>
    Object.values(obstructions)
      .filter(obstruction => obstruction.id !== id)
      .reduce<{ positions: SnapPoint[]; corners: SnapPoint[] }>(
        (result, { position, positions }) => {
          result.positions.push({ id: '', point: vectorModelToUI(position) })
          result.corners.push(...positions.map(p => ({ id: '', point: vectorModelToUI(p) })))
          return result
        },
        { positions: [], corners: [] }
      )
)

function AnObstruction({ obstruction }: { obstruction: Obstruction }) {
  const otherObstructionPoints = useAppSelector(state => selectOtherObstructionsPositions(state, obstruction.id))
  const selectedObjects = useAppSelector(state => state.selectedObjects)
  const isOnlyOneSelection = selectedObjects.length === 1
  const isSelected = selectedObjects.some(sel => sel.id === obstruction.id)
  const isUnlocked = useAppSelector(state => !state.layers.layers.OBSTRUCTIONS.locked)
  const isVisible = useAppSelector(state => state.layers.layers.OBSTRUCTIONS.visible)
  const isSnapEnabled = useAppSelector(state => state.tools.isSnapEnabled)
  const isOrthoModeEnabled = useAppSelector(state => state.tools.isOrthoModeEnabled)
  const isShiftHeld = useModifiers(modifiers => modifiers.shift)
  const dispatch = useAppDispatch()

  const [isDragging, setIsDragging] = useState(false)

  const rotationZ = MathUtils.degToRad(obstruction.rotation.z)
  const euler = new Euler()
  const newPosition = new Vector3()
  const rotatedDelta = new Vector3()

  const obstructionGroupRef = useRef<Group>(null!)
  const handlesRef = useRef<Group>(null)
  const rotationHandleRef = useRef<Group>(null!)
  const dimensionGuidesRef = useRef<ArrowHandle>(null)
  const snaplineRef = useRef<ObstructionsSnaplinesRef>(null!)

  const height = modelToUI(obstruction.height)
  const isStartingFromCeiling = obstruction.startLocation === 'ceiling'
  const isBasicObstruction = obstruction.obstructionType === 'basic'

  const { position, positions, rotationHandlePosition, handles, bounding, width, length } = useMemo(() => {
    const position = vectorModelToUI(obstruction.position)
    if (isStartingFromCeiling) {
      const distance = productDistanceEngine.getDistanceToMountPoint(obstruction.position) ?? 0
      position.setZ(modelToUI(distance - (obstruction.height + (obstruction.offset ?? 0))))
    } else {
      position.setZ(modelToUI(obstruction.offset ?? 0))
    }
    const positions = obstruction.positions.map(p => vectorModelToUI(p).sub(position))
    const { handles, extrema, ...points } = cornerPoints(positions, height)
    const rotationHandlePosition = new Vector3(extrema.maxX + 8, (extrema.minY + extrema.maxY) / 2, height / 2)
    return { position, positions, rotationHandlePosition, handles, ...points }
  }, [isStartingFromCeiling, obstruction.position, height, obstruction.offset, obstruction.positions])

  const snapToOriginCardinals = useCardinalSnaplines(position)

  const handleDrag = useCallback(
    (delta: Vector3) => {
      newPosition.copy(delta).setZ(0).add(position)
      if (isStartingFromCeiling) {
        const distance = productDistanceEngine.getDistanceToMountPoint(scaleUIVectorToModel(newPosition))
        if (distance != null) {
          const height = modelToUI(distance - (obstruction.height + (obstruction.offset ?? 0)))
          scaleModelVectorToUI(newPosition).setZ(height)
        }
      }
      snaplineRef.current.hide()
      const cardinalSnaps = snapToOriginCardinals(newPosition)
      if ((isOrthoModeEnabled || isShiftHeld) && isOnlyOneSelection && cardinalSnaps) {
        newPosition.copy(cardinalSnaps.point)
        snaplineRef.current.setPositions(cardinalSnaps.positions)
        snaplineRef.current.show()
      } else if (isSnapEnabled && isOnlyOneSelection) {
        const snappedOrthoCenterPoint = snapToPointOrtho(otherObstructionPoints.positions, newPosition)
        const snappedOrthoCornerPoint = positions.reduce<SnapData | undefined>((snapData, current) => {
          const snapped = snapToPointOrtho(otherObstructionPoints.corners, current.clone().add(newPosition))
          if (snapped) {
            const { objectLine, snapPoint } = snapped
            const prevDistance = snapData?.objectLine.distanceSq() ?? Infinity
            const currentDistance = objectLine.distanceSq()
            snapData = prevDistance < currentDistance ? snapData : { ...snapped, snapPoint: snapPoint.sub(current) }
          }
          return snapData
        }, undefined)
        const snappedOrthoPoint =
          (snappedOrthoCenterPoint?.distance ?? Infinity) < (snappedOrthoCornerPoint?.distance ?? Infinity)
            ? snappedOrthoCenterPoint
            : snappedOrthoCornerPoint
        if (snappedOrthoPoint && snappedOrthoPoint.objectLine.distanceSq() < SNAP_THRESHOLD) {
          const { objectLine, snapPoint } = snappedOrthoPoint
          newPosition.copy(snapPoint)
          const heightOffset = height + (isStartingFromCeiling ? 0 : newPosition.z) + SNAP_LINE_Z_OFFSET
          const [start, end] = [objectLine.start.clone().setZ(heightOffset), objectLine.end.clone().setZ(heightOffset)]
          snaplineRef.current.setPositions([...start.toArray(), ...end.toArray()])
          snaplineRef.current.show()
        }
      }
      obstructionGroupRef.current.position.copy(newPosition)
      handlesRef.current?.position.copy(newPosition).add(RESIZE_HANDLE_Z_OFFSET)
      rotationHandleRef.current.position.copy(newPosition.setZ(0))
      dimensionGuidesRef.current?.setOrigin(newPosition.setZ(0), { width, length, rotation: rotationZ })
    },
    [
      isStartingFromCeiling,
      height,
      isShiftHeld,
      isOnlyOneSelection,
      position,
      positions,
      obstruction.height,
      obstruction.offset,
      rotationZ,
      width,
      length,
      isSnapEnabled,
      isOrthoModeEnabled,
      otherObstructionPoints,
    ]
  )

  useEffect(() => {
    if (!isSelected) return
    const controller = new AbortController()
    window.addEventListener('multidrag', ({ detail }) => handleDrag(detail.totalDelta), { signal: controller.signal })
    return () => controller.abort()
  }, [isSelected, handleDrag])

  useEffect(() => {
    dimensionGuidesRef.current?.setOrigin(position.clone().setZ(0), { width, length, rotation: rotationZ })
    handlesRef.current?.children.forEach((group, i) => group.position.copy(handles[i]))
    handlesRef.current?.position.copy(position).add(RESIZE_HANDLE_Z_OFFSET)
    if (handlesRef.current) handlesRef.current.rotation.z = rotationZ
    obstructionGroupRef.current.scale.set(1, 1, 1)
    obstructionGroupRef.current.position.copy(position)
    obstructionGroupRef.current.rotation.z = rotationZ
    rotationHandleRef.current.position.copy(position).setZ(0)
    rotationHandleRef.current.children[0].position.copy(rotationHandlePosition)
    rotationHandleRef.current.rotation.z = rotationZ
  }, [handles, position, rotationZ, rotationHandlePosition, height, isSelected, width, length])

  const centroid = Util.polygonCentroid(obstruction.positions)
  const isError = !checkInFacility(obstruction.positions, centroid, rotationZ) && !obstruction.ignoreErrors

  const lineProps = {
    visible: isSelected && isUnlocked,
    color: 'black',
    dashed: true,
    lineWidth: 2,
  }

  return (
    <>
      <ObstructionSnaplines ref={snaplineRef} />
      {isSelected && isOnlyOneSelection && isVisible && (
        <DimensionGuides
          {...obstruction}
          dimensions={{ length: uiToModel(length), width: uiToModel(width) }}
          isDragging={isDragging}
          ref={dimensionGuidesRef}
          onUpdatePosition={newPosition => {
            const delta = new Vector3().copy(newPosition).sub(obstruction.position)
            const newPositions = obstruction.positions.map(p => ({ x: p.x + delta.x, y: p.y + delta.y, z: p.z }))
            dispatch(
              updateObstruction({ obstruction: { id: obstruction.id, position: newPosition, positions: newPositions } })
            )
          }}
        />
      )}
      <DragControls
        dragDisabled={!isSelected || !isUnlocked || !isOnlyOneSelection}
        onDragStart={() => setIsDragging(true)}
        onDrag={(_, delta) => handleDrag(delta)}
        onDragEnd={() => {
          setIsDragging(false)
          snaplineRef.current.hide()
          const newPosition = vectorUIToModel(obstructionGroupRef.current.position)
          const delta = new Vector3().copy(obstruction.position).sub(newPosition).negate()
          const { x, y, z } = newPosition
          dispatch(
            updateObstruction({
              obstruction: {
                id: obstruction.id,
                position: { x, y, z },
                positions: obstruction.positions.map(pt => ({ ...pt, x: pt.x + delta.x, y: pt.y + delta.y })),
              },
            })
          )
        }}
      >
        <group
          onClick={handleClick(e => {
            if (e.shiftKey) {
              const newSelectedObjects = isSelected
                ? selectedObjects.filter(object => object.id !== obstruction.id)
                : [...selectedObjects, { id: obstruction.id, className: ObjectClassName.OBSTRUCTION }]
              dispatch(selectObjects({ objects: newSelectedObjects }))
            } else {
              dispatch(selectObjects({ objects: [{ id: obstruction.id, className: ObjectClassName.OBSTRUCTION }] }))
            }
          })}
        >
          <group ref={obstructionGroupRef}>
            <Line {...lineProps} points={bounding.map(v => v.clone().setZ(height))} />
            <ObstructionClearanceMesh
              {...obstruction}
              isError={isError}
              isSelected={isSelected}
              isOnlySelected={isSelected && isOnlyOneSelection}
              bounds={positions.map(pt => new Vector2().copy(pt))}
            />
            <Line {...lineProps} points={bounding} />
            <Suspense fallback={<LoadingIndicator />}>
              {!isBasicObstruction && <DetailedObstructionMesh obstruction={obstruction} />}
            </Suspense>
          </group>
          {obstruction.resizable && (
            <group ref={handlesRef}>
              {RESIZE_HANDLES.map(index => {
                return (
                  <BoxDragControl
                    key={index}
                    dragDisabled={!obstruction.resizable}
                    visible={isSelected && isOnlyOneSelection && isUnlocked}
                    onDragStart={() => setIsDragging(true)}
                    onDrag={(e, delta) => {
                      snaplineRef.current.hide()
                      if (isSnapEnabled) {
                        const snapBase = delta.clone().add(handles[index]).add(position).setZ(0)
                        const snapped = snapToPointOrtho(otherObstructionPoints.corners, snapBase)
                        if (snapped) {
                          delta.copy(snapped.snapPoint).sub(handles[index]).sub(position)
                          const heightOffset = height + (isStartingFromCeiling ? 0 : newPosition.z) + SNAP_LINE_Z_OFFSET
                          const { objectLine } = snapped
                          const [start, end] = [objectLine.start.setZ(heightOffset), objectLine.end.setZ(heightOffset)]
                          snaplineRef.current.setPositions([...start.toArray(), ...end.toArray()])
                          snaplineRef.current.show()
                        }
                      }
                      const oppositeIndex = (index + Math.trunc(RESIZE_HANDLE_COUNT / 2)) % RESIZE_HANDLE_COUNT
                      rotatedDelta.set(delta.x, delta.y, 0).applyEuler(euler.set(0, 0, -rotationZ))
                      const mtx = transformMatrix(
                        handles[oppositeIndex],
                        handles[index],
                        { x: rotatedDelta.x, y: rotatedDelta.y, z: 0 },
                        e.shiftKey
                      )
                      handlesRef.current?.children.forEach((group, i) =>
                        group.position.copy(handles[i]).applyMatrix4(mtx)
                      )
                      newPosition
                        .setLength(0)
                        .applyMatrix4(mtx)
                        .applyEuler(euler.set(0, 0, rotationZ))
                        .add(position)
                      obstructionGroupRef.current.position.copy(newPosition)
                      obstructionGroupRef.current.scale.setFromMatrixScale(mtx)
                      rotationHandleRef.current.children[0].position.copy(rotationHandlePosition).applyMatrix4(mtx)
                      const { width, length } = cornerPoints(
                        positions.map(p => p.clone().applyMatrix4(mtx)),
                        position.z + height
                      )
                      dimensionGuidesRef.current?.setOrigin(newPosition, { width, length, rotation: rotationZ })
                    }}
                    onDragEnd={() => {
                      setIsDragging(false)
                      snaplineRef.current.hide()
                      newPosition.copy(vectorUIToModel(obstructionGroupRef.current.position))
                      const newPositions = obstruction.positions.map(p => {
                        const updatedPosition = new Vector3().copy(p).sub(obstruction.position)
                        updatedPosition.multiply(obstructionGroupRef.current.scale)
                        updatedPosition.add(newPosition)
                        return { x: updatedPosition.x, y: updatedPosition.y, z: updatedPosition.z }
                      })
                      dispatch(
                        updateObstruction({
                          obstruction: {
                            id: obstruction.id,
                            position: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
                            positions: newPositions,
                          },
                        })
                      )
                    }}
                  />
                )
              })}
            </group>
          )}
        </group>
      </DragControls>
      <group ref={rotationHandleRef}>
        <ScreenSizer>
          <RotationHandle
            rotation-x={Math.PI / 2}
            visible={isSelected && isOnlyOneSelection && isUnlocked}
            center={position}
            onRotate={angle => {
              if (handlesRef.current) handlesRef.current.rotation.z = angle
              rotationHandleRef.current.rotation.z = angle
              obstructionGroupRef.current.rotation.z = angle
            }}
            onCommit={angle => {
              dispatch(
                updateObstruction({
                  obstruction: {
                    id: obstruction.id,
                    rotation: { x: 0, y: 0, z: MathUtils.radToDeg(angle) },
                  },
                })
              )
            }}
          />
        </ScreenSizer>
      </group>
    </>
  )
}
